import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import "./LandingPage.scss";
import axios from 'axios'
import heartLoading from "../../../assests/heartLoading.gif"
import ProductViewModal from "./ProductViewModal.jsx";
import MuiDataTable from "./MuiDataTable.jsx";
import { Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { isProdEnv } from "../../../env-utils.js";


const LandingPage = () => {
  
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("products");
  const [products, setProducts] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);

  useEffect(() => {
    getchProductsDetails();
  }, []);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const getchProductsDetails = async() => {
    try{
      setProductsLoading(true)
      setLoading(true)
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-product-submissions`)

      if(response.status === 200 || 201){
        setProducts(response.data)
      }
    
    }catch(e){
      setError("Failed to fetch products. Please try again later.")
    } finally {
      setProductsLoading(false)
      setLoading(false)
    }
  }

  // Filter products by search query
  const currentDataSource = products.filter(product => 
    searchQuery ? product.productName.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const rowsPerPage = 10;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = currentDataSource.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(currentDataSource.length / rowsPerPage));

  const handleViewProduct = (product) => {
    if (product) {
      setSelectedProductDetails(product);
      setShowViewModal(true);
    }
  };

  const handleDeleteProduct = async(product_id) => { 
    try{
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/delete-product/${product_id}`)
      if (response?.status === 200) {
        setToast({ open: true, message: 'Product deleted successfully.', severity: 'success' });
        getchProductsDetails();
      }
    } catch (err) {
      setToast({ open: true, message: 'Failed to delete product.', severity: 'error' });
    }
  }

  const role = localStorage.getItem("role") || "";

  const productColumns = [
    { id: 'productName', label: 'Product Name', sortable: true },
    { id: 'productDescription', label: 'Description', sortable: true },
    { id: 'version', label: 'Version', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'currentPhase', label: 'Current Phase', sortable: true},
    { id: 'versioning', label: 'Versioning', sortable: true},
    { id: 'actions', label: 'Actions', sortable: false }
  ];

  if (loading || productsLoading) {
    return (
     <div className="relative bg-white flex items-center justify-center z-50 min-h-screen">
             <div className="flex flex-col items-center space-y-3">
               <img src={heartLoading} alt="Loading..." width={200} />
               <span className="text-gray-700 text-[20px] text-center">
Hang tight! The resources are loading...
               </span>
             </div>
           </div>
    );
  }

  if (error) {
    return (
      <div className="error-container ">
        <p className="error-message">{error}</p>
        <button onClick={getchProductsDetails} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: "0px", paddingRight: "0px", backgroundColor: "#f9fafb" }} className="dashboard-container">
      <Snackbar 
        open={toast.open} 
        autoHideDuration={3000} 
        onClose={() => setToast({ ...toast, open: false })} 
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <div className="dashboard-main">
        <div className="dashboard-header px-4">
          <div className="font-bold font-family-poppins pt-2 text-[24px] text-[#424242]">Overview</div>
          <p className="pb-4">View all essential information about this asset in one place</p>

          {/* Top Actions Bar */}
          <div className="dashboard-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {!isProdEnv() && <>
                {(role === 'Data Steward' || role === "Admin" || role === "Domain Owner" || role === "Product Owner") ? (
                <Link 
                  to="/product-onboarding" 
                  className="onboard-button h-[50px] w-[180px] rounded-md border border-[#818181] flex items-center justify-center whitespace-nowrap"
                >
                  <span className="flex items-center gap-1">
                    <AddIcon fontSize="small" /> Onboard Product
                  </span>
                </Link>
                ) : (
                  <button 
                    disabled 
                    style={{ color:"#333", cursor:"not-allowed", padding:'4px 6px' }} 
                    className="onboard-button bg-gray-100 h-[50px] w-[140px] rounded-md border border-[#818181]"
                  >
                    <span><AddIcon /> New Product</span>
                  </button>
                )}
              </>}

              {/* Search Box */}
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search for Products"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className={`px-3 py-2 border rounded text-base font-medium ${
                  currentPage === 1
                    ? 'opacity-50 cursor-not-allowed text-gray-400 bg-gray-50'
                    : 'text-gray-700 bg-white hover:bg-gray-100'
                }`}
              >
                Previous
              </button>

              {(() => {
                const pageButtons = [];
                const totalToShow = 5;
                let start = Math.max(1, currentPage - Math.floor(totalToShow / 2));
                let end = start + totalToShow - 1;

                if (end > totalPages) {
                  end = totalPages;
                  start = Math.max(1, end - totalToShow + 1);
                }

                if (start > 1) pageButtons.push(1);
                if (start > 2) pageButtons.push("...");
                for (let i = start; i <= end; i++) pageButtons.push(i);
                if (end < totalPages - 1) pageButtons.push("...");
                if (end < totalPages) pageButtons.push(totalPages);

                return pageButtons.map((p, idx) =>
                  p === "..." ? (
                    <span key={idx} className="px-2 text-gray-500">...</span>
                  ) : (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-2 border rounded text-base font-medium ${
                        currentPage === p
                          ? 'bg-[#bb1ccc] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  )
                );
              })()}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className={`px-3 py-2 border rounded text-base font-medium ${
                  currentPage === totalPages
                    ? 'opacity-50 cursor-not-allowed text-gray-400 bg-gray-50'
                    : 'text-gray-700 bg-white hover:bg-gray-100'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-300 mb-4"></div>

        {/* Table */}
        <div className="px-4">
          <div className="table-container">
            <MuiDataTable
              data={currentRows}
              columns={productColumns}
              onView={handleViewProduct}
              onDelete={handleDeleteProduct}
              selectedView={selectedView}
              searchQuery={searchQuery}
              userRole={role}
            />
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && (
        <ProductViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          product={selectedProductDetails}
        />
      )}
    </div>
  );
};

export default LandingPage;
